import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  getGifts,
  addGift as addGiftService,
  updateGift as updateGiftService,
  updateGiftStatus as updateGiftStatusService,
  deleteGift as deleteGiftService,
} from "../../services/giftService";   

import { toast } from "react-toastify";

const initialState = {
  gifts: [],
  status: "idle", // "idle" | "loading" | "succeeded" | "failed"
  error: null,
};

// fetch all the gift
export const fetchGifts = createAsyncThunk(
  "gifts//fetchGifts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getGifts();
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "An Error Occured");
      return rejectWithValue(
        error.response?.data?.message || "An Error Occured"
      );
    }
  }
);

// Add a new Gift
export const addGift = createAsyncThunk(
  "gifts/addGift",
  async (gift, { rejectWithValue }) => {
    try {
      const response = await addGiftService(gift);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to add Gift";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);


// Update a Gift
export const updateGift = createAsyncThunk(
  "gifts/updateGift",
  async ({ id, giftData }, { rejectWithValue }) => {
    try {
      const response = await updateGiftService(id, giftData);
      toast.success(response.message || "Gift updated successfully!");
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to Update ";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);



// Update a Gift Status
export const updateGiftStatus = createAsyncThunk(
  "gifts/updateGiftStatus",
  async ({ id, statusObj }, { rejectWithValue }) => {
    try {
      const response = await updateGiftStatusService(id, statusObj);
      toast.success(response.message || "Status Updated Successfully");
      return { id, statusObj, updatedData: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to Update Status ";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);



// Delete a Gift
export const deleteGift = createAsyncThunk(
  "gifts/deleteGift",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteGiftService(id);
      toast.success(response.message || "Gift deleted successfully!");
      return id;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete Gift";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const giftSlice = createSlice({
  name: "gifts",
  initialState,
  reducers: {
    clearError: (state) => {
      state.gifts = [];
      state.status = "idle";
      state.error = null;
      state.totalCount = 0;
    },
    setGifts: (state, action) => {
      state.gifts = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      //fetch gift
      .addCase(fetchGifts.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchGifts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.gifts = action.payload.data || [];
        state.totalCount =
          action.payload.totalCount || action.payload.data?.length || 0;
        state.error = null;
      })
      .addCase(fetchGifts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.gifts = [];
      })

      // Add Gift
      .addCase(addGift.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addGift.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.data) {
          state.gifts.unshift(action.payload.data);
          state.totalCount += 1;
        }
        state.error = null;
      })
      .addCase(addGift.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Gift
      .addCase(updateGift.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateGift.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedGift = action.payload.data;

        if (updatedGift && Array.isArray(state.gifts)) {
          const index = state.gifts.findIndex(
            (gift) => gift._id === updatedGift._id
          );
          if (index !== -1) {
            state.gifts[index] = updatedGift;
          }
        }
        state.error = null;
      })
      .addCase(updateGift.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

// Update Gift Status
      .addCase(updateGiftStatus.pending, (state) => {})
      .addCase(updateGiftStatus.fulfilled, (state, action) => {
        const { id, statusObj, updatedData } = action.payload;

        if (Array.isArray(state.gifts)) {
          const index = state.gifts.findIndex((gift) => gift._id === id);
          if (index !== -1) {
            if (updatedData) {
              state.gifts[index] = updatedData;
            } else {
              state.gifts[index] = { ...state.gifts[index], ...statusObj };
            }
          }
        }
      })
      .addCase(updateGiftStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

  // Delete Gift
      .addCase(deleteGift.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteGift.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.payload;
        state.gifts = state.gifts.filter((gift) => gift._id !== deletedId);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deleteGift.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
      
  },
});


export const { clearError, resetGiftState, setGifts } = giftSlice.actions;


// Selectors
export const selectAllGifts = (state) => state.gifts.gifts;
export const selectGiftsById = (state, giftId) => 
  state.gifts.gifts.find(gift => gift._id === giftId);
export const selectGiftStatus = (state) => state.gifts.status;
export const selectGiftError = (state) => state.gifts.error;
export const selectGiftTotalCount = (state) => state.gifts.totalCount;
export const selectActiveGifts = (state) => 
  state.gifts.gifts.filter(gift => gift.status === 'Active');
export const selectPopularGifts = (state) => 
  state.gifts.gifts.filter(gift => gift.isPopular === true);

export default giftSlice.reducer;